import React, { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Confetti from "react-confetti"
import { playAgain, getIdentity } from "./ballparkRoom"
import { Screen, BpButton, PlayerDot, C, F } from "./BallparkUI"
import { playSound } from "../../../utils/ballparkSounds"

const useWindowSize = () => {
  const [size, setSize] = useState({ width: window.innerWidth, height: window.innerHeight })
  useEffect(() => {
    const handleResize = () => setSize({ width: window.innerWidth, height: window.innerHeight })
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])
  return size
}

const GameOverScreen = ({ room, code, isHost }) => {
  const navigate = useNavigate()
  const { width, height } = useWindowSize()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  useEffect(() => {
    playSound("gameover")
  }, [])

  const standings = Object.entries(room.players).sort(
    (a, b) => b[1].score - a[1].score
  )
  if (!standings.length) return null
  const champ = standings[0]
  const maxScore = champ[1].score
  const champions = standings.filter(([pid, p]) => p.score === maxScore)
  const isTie = champions.length > 1

  const handlePlayAgain = async () => {
    setBusy(true)
    setError("")
    try {
      playSound("click")
      const newCode = await playAgain(code, getIdentity().name)
      navigate(`/ballpark/${newCode}`)
    } catch (e) {
      setError("Couldn't start a new game. Try again.")
      setBusy(false)
    }
  }

  return (
    <Screen style={{ gap: 18 }}>
      {maxScore > 0 && (
        <Confetti width={width} height={height} recycle={false} numberOfPieces={180} />
      )}

      <div style={{ textAlign: "center", marginTop: 16 }}>
        <div style={{ fontSize: 30 }}>👑</div>
        <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 30, marginTop: 4, lineHeight: 1.2 }}>
          {isTie
            ? `${champions.map(([pid, p]) => p.name).join(" & ")} tie for the win!`
            : `${champ[1].name} takes it!`}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.muted, marginTop: 4 }}>
          {room.totalRounds} rounds · {standings.length} players
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {standings.map(([pid, p], i) => {
          const isWinner = p.score === maxScore
          const rank = standings.findIndex(([_, pl]) => pl.score === p.score) + 1
          return (
            <div
              key={pid}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                background: isWinner ? C.highlight : C.card,
                border: isWinner ? `2.5px solid ${C.gold}` : `1.5px solid ${C.border}`,
                borderRadius: 16,
                padding: "14px 16px",
                boxShadow: isWinner ? `0 3px 0 ${C.gold}` : "none",
              }}
            >
              <span
                style={{
                  fontFamily: F.display,
                  fontWeight: 800,
                  fontSize: 18,
                  width: 24,
                  color: isWinner ? C.ink : C.muted,
                }}
              >
                {rank}
              </span>
              <PlayerDot color={p.color} size={34} />
              <span style={{ fontWeight: 800, fontSize: 17 }}>{p.name}</span>
              <span
                style={{
                  marginLeft: "auto",
                  fontFamily: F.display,
                  fontWeight: 800,
                  fontSize: 24,
                  color: isWinner ? C.rust : C.ink,
                }}
              >
                {p.score}
              </span>
            </div>
          )
        })}
      </div>

      {room.history && room.history.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontFamily: F.display, fontWeight: 800, fontSize: 20, marginBottom: 12 }}>
            Match Breakdown
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {room.history.map((h, idx) => (
              <div
                key={idx}
                style={{
                  background: C.card,
                  border: `1.5px solid ${C.border}`,
                  borderRadius: 16,
                  padding: "16px 18px",
                  boxShadow: `0 3px 0 ${C.border}`,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span
                    style={{
                      background: C.rust,
                      color: C.bg,
                      fontWeight: 800,
                      fontSize: 11,
                      padding: "3px 8px",
                      borderRadius: 8,
                      letterSpacing: 0.5,
                    }}
                  >
                    Round {h.round}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: C.goldDark }}>
                    Answer: {h.answer}
                  </span>
                </div>

                <div style={{ fontFamily: F.display, fontWeight: 700, fontSize: 15, marginTop: 10, color: C.ink, lineHeight: 1.3 }}>
                  {h.question}
                </div>

                <div style={{ fontSize: 12, color: C.muted, marginTop: 4, fontStyle: "italic", lineHeight: 1.3 }}>
                  {h.note}
                </div>

                <div style={{ borderTop: `1px dashed ${C.border}`, marginTop: 12, paddingTop: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: C.muted, letterSpacing: 0.5, marginBottom: 6 }}>
                    GUESSES & SCORE DELTAS
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {Object.entries(room.players).map(([pid, p]) => {
                      const guess = h.guesses?.[pid]
                      const delta = h.deltas?.[pid] || 0
                      return (
                        <div
                          key={pid}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            fontSize: 13,
                          }}
                        >
                          <PlayerDot color={p.color} size={16} />
                          <span style={{ fontWeight: 700 }}>{p.name}:</span>
                          <span style={{ color: C.ink }}>{guess !== undefined ? guess : "No guess"}</span>
                          {delta > 0 && (
                            <span
                              style={{
                                marginLeft: "auto",
                                fontWeight: 800,
                                color: C.rust,
                                fontSize: 12,
                              }}
                            >
                              +{delta} pts
                            </span>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: C.rust, fontWeight: 700, fontSize: 14, textAlign: "center" }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: "auto", display: "flex", gap: 10, paddingTop: 12 }}>
        {isHost && (
          <BpButton onClick={handlePlayAgain} disabled={busy} style={{ flex: 1 }}>
            {busy ? "Setting up…" : "Play again"}
          </BpButton>
        )}
        <BpButton
          variant="panel"
          onClick={() => {
            playSound("click")
            navigate("/ballpark")
          }}
          style={{ flex: 1 }}
        >
          Back home
        </BpButton>
      </div>
    </Screen>
  )
}

export default GameOverScreen
